import { resolveApplicantFieldValue, type EsignFieldType } from './pdfFieldRenderer';

/**
 * Shared catalogue of applicant/application data fields that a signing
 * field can be linked to (the field's `fieldMapping`).
 *
 * This module is the single source of truth for the link picker: the
 * template editor (PdfFieldEditor) renders its "Applicant Auto-Fill
 * Field" select from APPLICANT_FIELD_CATALOGUE, and the server-side
 * create core resolves stored mappings through
 * resolveLinkedFieldValue() when a sign request is generated.
 *
 * Every entry resolves against a real path on the application record —
 * the same paths the merge-field resolver's simpleTagMap reads
 * (src/lib/variables/resolver.ts). The list is curated and
 * applicant-focused; it is NOT the full merge-token catalogue.
 */

export interface ApplicantFieldOption {
  label: string;
  /** Dot path on the application record, stored as the field's fieldMapping. */
  path: string;
}

export interface ApplicantFieldGroup {
  group: string;
  options: ApplicantFieldOption[];
}

/**
 * Special mapping key for the composed residential address. There is no
 * single stored address string on the record — the address lives as
 * components under personalDetails.address — so this key is resolved by
 * composition (see composeResidentialAddress), never by dot-path
 * (a dot-path read of the address object would stringify the object).
 */
export const COMPOSED_RESIDENTIAL_ADDRESS_KEY = 'personalDetails.address';

export const APPLICANT_FIELD_CATALOGUE: ApplicantFieldGroup[] = [
  {
    group: 'Personal Details',
    options: [
      { label: 'Full Name', path: 'personalDetails.fullName' },
      { label: 'First Name', path: 'personalDetails.firstName' },
      { label: 'Last Name', path: 'personalDetails.lastName' },
      { label: 'Middle Name', path: 'personalDetails.middleName' },
      { label: 'Email Address', path: 'userEmail' },
      { label: 'Phone Number', path: 'personalDetails.phone' },
      { label: 'Mobile Number', path: 'personalDetails.mobileNumber' },
      { label: 'Date of Birth', path: 'personalDetails.dob' },
      { label: 'Gender', path: 'personalDetails.gender' },
      { label: 'Marital Status', path: 'personalDetails.maritalStatus' },
      { label: 'Citizenship Country', path: 'personalDetails.countryOfCitizenship' },
      { label: 'Country of Birth', path: 'personalDetails.countryOfBirth' },
      { label: 'City of Birth', path: 'personalDetails.cityOfBirth' },
    ],
  },
  {
    group: 'Address',
    options: [
      { label: 'Residential Address', path: COMPOSED_RESIDENTIAL_ADDRESS_KEY },
      { label: 'Street', path: 'personalDetails.address.street' },
      { label: 'City', path: 'personalDetails.address.city' },
      { label: 'State / Province', path: 'personalDetails.address.state' },
      { label: 'ZIP / Postal Code', path: 'personalDetails.address.zip' },
      { label: 'Country', path: 'personalDetails.address.country' },
    ],
  },
  {
    group: 'Passport & Visa',
    options: [
      { label: 'Passport Number', path: 'passportImmigration.passportNumber' },
      { label: 'Passport Country', path: 'passportImmigration.passportCountry' },
      { label: 'Passport Issue Date', path: 'passportImmigration.issueDate' },
      { label: 'Passport Expiry Date', path: 'passportImmigration.expirationDate' },
      { label: 'Visa Type', path: 'visaDetails.visaType' },
      { label: 'Visa Number', path: 'visaDetails.visaNumber' },
      { label: 'DS-160 Number', path: 'ds160Details.ds160Number' },
    ],
  },
  {
    group: 'Program',
    options: [
      { label: 'Program Name', path: 'programName' },
      { label: 'Program Type', path: 'programType' },
    ],
  },
  {
    group: 'Emergency Contact',
    options: [
      { label: 'Emergency Contact Name', path: 'emergencyContacts.0.fullName' },
      { label: 'Emergency Contact Phone', path: 'emergencyContacts.0.phone' },
      { label: 'Emergency Contact Relationship', path: 'emergencyContacts.0.relationship' },
    ],
  },
  {
    group: 'Application System',
    options: [
      { label: 'Application ID', path: 'applicationId' },
      { label: 'User ID', path: 'userId' },
    ],
  },
];

/**
 * Field types the editor offers the link picker for: the text-bearing
 * types a stored value can meaningfully pre-fill. Stored mappings on
 * any other type (older templates) still resolve server-side at
 * request creation — only the editor stops offering new ones there.
 */
export const LINKABLE_FIELD_TYPES: readonly EsignFieldType[] = [
  'text',
  'multilineText',
  'email',
  'phone',
  'date',
  'number',
  'currency',
  'dropdown',
  'address',
  'companyName',
  'jobTitle',
];

export function isLinkableFieldType(fieldType: EsignFieldType | string | undefined): boolean {
  return !!fieldType && (LINKABLE_FIELD_TYPES as readonly string[]).includes(fieldType);
}

/** Human label for a stored mapping key, or undefined when not catalogued. */
export function applicantFieldLabel(mappingKey: string): string | undefined {
  if (!mappingKey) return undefined;
  for (const grp of APPLICANT_FIELD_CATALOGUE) {
    for (const opt of grp.options) {
      if (opt.path === mappingKey) return opt.label;
    }
  }
  return undefined;
}

/**
 * Compose the residential address from its stored components, joining
 * with commas and skipping empties — mirrors the merge resolver's
 * `address` tag (src/lib/variables/resolver.ts). Returns '' when the
 * record carries no address components at all.
 */
export function composeResidentialAddress(appData: any): string {
  const addr = appData?.personalDetails?.address;
  if (!addr || typeof addr !== 'object') return '';
  const parts = [addr.street, addr.city, addr.state, addr.zip, addr.country]
    .map((v) => (v != null ? String(v).trim() : ''))
    .filter(Boolean);
  return parts.join(', ');
}

/**
 * Keys whose canonical value follows the merge resolver's own
 * precedence rather than a single dot path (simpleTagMap reads these
 * from more than one place on the record). The catalogue stores the
 * first key; resolution tries each path in order.
 */
const RESOLUTION_FALLBACKS: Record<string, string[]> = {
  // simpleTagMap program_name: assignment.programName || programName || programType
  programName: ['assignment.programName', 'programName', 'programType'],
  // simpleTagMap visa_number: visaDetails.visaNumber || passportImmigration.visaNumber
  'visaDetails.visaNumber': ['visaDetails.visaNumber', 'passportImmigration.visaNumber'],
  // simpleTagMap ds160_number: ds160Details.ds160Number || visaDetails.ds160ConfirmationNumber
  'ds160Details.ds160Number': ['ds160Details.ds160Number', 'visaDetails.ds160ConfirmationNumber'],
  // simpleTagMap emergency_name: emergencyContacts[0].fullName || .name
  'emergencyContacts.0.fullName': ['emergencyContacts.0.fullName', 'emergencyContacts.0.name'],
};

/**
 * Resolve a signing field's linked value against an application record.
 * Returns '' when the record holds no value for the mapping — callers
 * treat '' as "missing" and must never substitute a placeholder.
 *
 * `appData` is expected to be the PII-DECRYPTED record: values that are
 * still ciphertext blocks resolve to '' here (a block stringifies to
 * nothing usable), so encrypted data can never leak into a document.
 */
export function resolveLinkedFieldValue(appData: any, mappingKey: string): string {
  if (!appData || !mappingKey) return '';
  if (mappingKey === COMPOSED_RESIDENTIAL_ADDRESS_KEY) {
    return composeResidentialAddress(appData);
  }
  const fallbacks = RESOLUTION_FALLBACKS[mappingKey];
  if (fallbacks) {
    for (const path of fallbacks) {
      const value = resolveApplicantFieldValue(appData, path);
      if (value && value !== '[object Object]') return value;
    }
    return '';
  }
  const raw = resolveApplicantFieldValue(appData, mappingKey);
  // resolveApplicantFieldValue stringifies whatever sits at the path;
  // an object there (e.g. an un-decrypted PII block or a nested record)
  // is not a usable field value.
  if (raw === '[object Object]') return '';
  return raw;
}
